/**
 * preloadImages.js
 *
 * Lightweight image preloader used during initial app load to warm the
 * browser cache for footer quick-nav thumbnails, header/welcome logos,
 * and any other UI imagery that would otherwise pop in on first render.
 *
 * preloadImages(urls, onProgress?) → Promise<{ loaded, failed, total }>
 *
 *   urls       — array of absolute or root-relative image URLs
 *   onProgress — (done, total) called after each image resolves (success
 *                or failure) so callers can drive a progress UI
 */

export default function preloadImages(urls, onProgress) {
  const list = Array.from(new Set((urls || []).filter(Boolean)));
  const total = list.length;
  if (total === 0) return Promise.resolve({ loaded: 0, failed: 0, total: 0 });

  let done = 0;
  let loaded = 0;
  let failed = 0;

  return new Promise((resolve) => {
    list.forEach((src) => {
      const img = new Image();
      const finish = (ok) => {
        if (ok) loaded += 1; else failed += 1;
        done += 1;
        try { onProgress?.(done, total); } catch (_) { /* ignore */ }
        if (done === total) resolve({ loaded, failed, total });
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      // Encoded src so paths with spaces (e.g. "MANDIR AREA.jpeg") work.
      img.src = encodeURI(src);
    });
  });
}
